import { useCallback, useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowLeft, X } from "lucide-react";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  buildWhatsAppUrl,
  noCnpjCopy,
  type ContactChannel,
} from "./contactChannels";
import { useCnpjValidation, type CompanyInfo } from "./useCnpjValidation";
import {
  BlockedStep,
  ChannelSelectionStep,
  CnpjEntryStep,
  CnpjQuestionStep,
  CnpjSuccessStep,
  NoCnpjStep,
} from "./steps";

type Step =
  | "channel-selection"
  | "cnpj-question"
  | "cnpj-entry"
  | "success"
  | "no-cnpj"
  | "inactive-company"
  | "not-found";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function openWhatsApp(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

export default function WhatsAppContactModal({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<Step>("channel-selection");
  const [channel, setChannel] = useState<ContactChannel | null>(null);
  const [cnpj, setCnpj] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const { validate, loading, reset } = useCnpjValidation();

  const resetAll = useCallback(() => {
    setStep("channel-selection");
    setChannel(null);
    setCnpj("");
    setError(null);
    setCanRetry(false);
    setCompany(null);
    reset();
  }, [reset]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(resetAll, 200);
      return () => clearTimeout(t);
    }
  }, [open, resetAll]);

  const handleSelectChannel = (c: ContactChannel) => {
    setChannel(c);
    if (!c.requiresCnpj) {
      openWhatsApp(buildWhatsAppUrl(c));
      onOpenChange(false);
      return;
    }
    setStep("cnpj-question");
  };

  const handleAnswer = (hasCnpj: boolean) => {
    setStep(hasCnpj ? "cnpj-entry" : "no-cnpj");
  };

  const handleValidate = async () => {
    if (loading) return;
    setError(null);
    setCanRetry(false);
    const outcome = await validate(cnpj);
    switch (outcome.kind) {
      case "active":
        setCompany(outcome.company);
        setStep("success");
        break;
      case "invalid":
        setError("CNPJ inválido. Verifique o número informado.");
        break;
      case "not_found":
        setStep("not-found");
        break;
      case "inactive":
        setStep("inactive-company");
        break;
      default:
        setError("Não foi possível validar o CNPJ neste momento. Tente novamente em alguns instantes.");
        setCanRetry(true);
    }
  };

  const handleBack = () => {
    setError(null);
    setCanRetry(false);
    if (step === "cnpj-entry" || step === "no-cnpj") setStep("cnpj-question");
    else if (step === "cnpj-question") setStep("channel-selection");
    else if (step === "success" || step === "not-found" || step === "inactive-company") setStep("cnpj-entry");
    else setStep("channel-selection");
  };

  const backToChannels = () => {
    setStep("channel-selection");
    setChannel(null);
    setCnpj("");
    setError(null);
    setCompany(null);
  };

  const headings: Record<Step, { title: string; description?: string }> = {
    "channel-selection": {
      title: "Como podemos ajudar?",
      description: "Escolha o setor com o qual deseja falar.",
    },
    "cnpj-question": {
      title: "Você possui CNPJ?",
      description: "Para este tipo de atendimento precisamos confirmar os dados da empresa.",
    },
    "cnpj-entry": {
      title: "Informe o CNPJ da empresa",
      description: "Vamos fazer uma validação rápida antes de continuar.",
    },
    success: { title: "Tudo certo!", description: "Confirmamos os dados da empresa." },
    "no-cnpj": { title: "Atendimento empresarial" },
    "inactive-company": { title: "Situação cadastral" },
    "not-found": { title: "CNPJ não localizado" },
  };

  const heading = headings[step];
  const showBack = step !== "channel-selection";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby={heading.description ? "wa-modal-desc" : undefined}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-0 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 motion-reduce:animate-none"
          style={{ maxHeight: "calc(100dvh - 2rem)" }}
        >
          <div className="relative px-5 pb-6 pt-5 sm:px-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
            <div className="mb-4 flex items-start justify-between gap-3">
              {showBack ? (
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label="Voltar"
                  className="-ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-brand-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : (
                <span className="h-10 w-0" />
              )}
              <DialogPrimitive.Close
                aria-label="Fechar"
                className="-mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>

            <div key={step} className="animate-fade-in">
              <DialogTitle className="font-heading text-xl font-black leading-tight text-brand-blue">
                {heading.title}
              </DialogTitle>
              {heading.description && (
                <DialogDescription id="wa-modal-desc" className="mt-1 text-sm text-slate-500">
                  {heading.description}
                </DialogDescription>
              )}
              {channel && step !== "channel-selection" && (
                <p className="mt-2 inline-flex rounded-full bg-brand-blue/5 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-brand-blue">
                  {channel.title}
                </p>
              )}

              <div className="mt-5">
                {step === "channel-selection" && <ChannelSelectionStep onSelect={handleSelectChannel} />}
                {step === "cnpj-question" && <CnpjQuestionStep onAnswer={handleAnswer} />}
                {step === "cnpj-entry" && (
                  <CnpjEntryStep
                    value={cnpj}
                    onChange={(v) => {
                      setCnpj(v);
                      setError(null);
                      setCanRetry(false);
                    }}
                    onSubmit={handleValidate}
                    loading={loading}
                    error={error}
                    canRetry={canRetry}
                  />
                )}
                {step === "no-cnpj" && channel && noCnpjCopy[channel.id] && (
                  <NoCnpjStep
                    title={noCnpjCopy[channel.id]!.title}
                    text={noCnpjCopy[channel.id]!.text}
                    onBack={backToChannels}
                  />
                )}
                {step === "not-found" && (
                  <BlockedStep
                    title="Não encontramos este CNPJ na base cadastral."
                    text="Confira o número informado e tente novamente."
                    onBack={() => setStep("cnpj-entry")}
                  />
                )}
                {step === "inactive-company" && (
                  <BlockedStep
                    title="CNPJ encontrado, porém a situação cadastral da empresa não está ativa."
                    text="Para seguir com este atendimento é necessário um CNPJ com situação cadastral ativa."
                    onBack={backToChannels}
                    backLabel="Voltar aos canais de atendimento"
                  />
                )}
                {step === "success" && company && channel && (
                  <CnpjSuccessStep
                    company={company}
                    onContinue={() => {
                      openWhatsApp(
                        buildWhatsAppUrl(channel, company),
                      );
                      onOpenChange(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
